import { cn } from '../../../../lib/utils';
import {
  Skeleton,
  SKELETON_REVEAL_CLASS,
  SkeletonGroup,
} from '../../../portal';

/** One 20px text line (14px bar), matching `FieldLabel` / body copy. */
function Line({ className }: { className: string }) {
  return (
    <span className="flex h-5 items-center">
      <Skeleton className={cn('h-3.5', className)} />
    </span>
  );
}

/** `Form.Input` box: label (+ explanation) → 4px → control → optional 16px hint. */
function Field({
  label,
  explanation,
  control = 'h-10 w-full',
  hint = false,
}: {
  label: string;
  explanation?: string;
  control?: string;
  hint?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col">
        <Line className={label} />
        {explanation ? <Line className={explanation} /> : null}
      </div>
      <Skeleton className={control} rounded="lg" />
      {hint ? (
        <span className="flex h-4 items-center">
          <Skeleton className="h-3 w-24" />
        </span>
      ) : null}
    </div>
  );
}

/**
 * Mirrors `DashboardDetailsForm` (same wrapper, column widths and field
 * boxes) so nothing shifts when `getDapp` lands: heading, App Name, Package
 * Name, Subtitle, Description, icon, banner + the 288px phone.
 */
export function StoreListingSkeleton() {
  return (
    <SkeletonGroup
      label="Loading store listing"
      className="flex flex-col gap-12 px-5 py-8 sm:px-[60px] sm:py-12 lg:flex-row lg:items-start lg:justify-between"
    >
      <div className="flex w-full max-w-[640px] flex-col gap-10">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full max-w-[540px]" />
          <Line className="w-3/4" />
        </div>

        <div className="flex flex-col gap-6">
          <Field label="w-20" hint />
          <Field label="w-28" explanation="w-3/4" />
          <Field label="w-16" explanation="w-2/3" hint />
          <Field label="w-24" explanation="w-1/2" control="h-[110px] w-full" />
          <Field label="w-36" explanation="w-3/4" control="size-[90px]" />
          <Field
            label="w-32"
            explanation="w-3/4"
            control="h-[90px] w-[180px]"
          />
        </div>
      </div>

      <div className="flex w-full shrink-0 flex-col items-center gap-5 lg:w-[300px]">
        <Line className="w-24" />
        <Skeleton className="h-[667.5px] w-72 rounded-[30px]" />
      </div>
    </SkeletonGroup>
  );
}

/** Header placeholder for the two 140px action buttons (Save Draft / Publish). */
export function StoreListingActionsSkeleton() {
  return (
    <span
      aria-hidden="true"
      className={cn('flex items-center gap-4', SKELETON_REVEAL_CLASS)}
    >
      <Skeleton className="h-10 w-[140px] rounded-xl" />
      <Skeleton className="h-10 w-[140px] rounded-xl" />
    </span>
  );
}
